import { useState } from "react";
import { Link as LinkIcon, Shield, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSecurity } from "@/security/ArmadilloProvider";
import { toast } from "sonner";

interface SafeProofLinkProps {
  url: string;
  className?: string;
}

/**
 * Owner-facing link proof: Armadillo scan before opening in a new tab.
 */
export function SafeProofLink({ url, className }: SafeProofLinkProps) {
  const { validateAction } = useSecurity();
  const [checking, setChecking] = useState(false);
  const [lastResult, setLastResult] = useState<"safe" | "unsafe" | null>(null);

  const openSafely = async () => {
    let parsed: URL;
    try {
      parsed = new URL(url.startsWith("http") ? url : `https://${url}`);
    } catch {
      toast.error("Invalid link URL");
      setLastResult("unsafe");
      return;
    }

    setChecking(true);
    setLastResult(null);
    try {
      const result = await validateAction("proof_submission", parsed.href);
      if (!result.allowed) {
        setLastResult("unsafe");
        toast.error(result.reason || "This link was blocked by Armadillo security");
        return;
      }
      setLastResult("safe");
      window.open(parsed.href, "_blank", "noopener,noreferrer");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className={`space-y-2 ${className ?? ""}`}>
      <div className="flex items-start gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border-2 border-emerald-200 dark:border-emerald-700">
        <LinkIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100 mb-2">Submitted link</p>
          <p className="text-sm text-muted-foreground break-all mb-3">{parsedDisplay(url)}</p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
            onClick={openSafely}
            disabled={checking}
          >
            {checking ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Shield className="h-4 w-4 mr-2" />
            )}
            {checking ? "Scanning link…" : "Verify & open link"}
            {!checking && <ExternalLink className="h-3.5 w-3.5 ml-2" />}
          </Button>
          {lastResult === "safe" && (
            <p className="text-xs text-green-600 mt-2">Armadillo: link cleared</p>
          )}
          {lastResult === "unsafe" && (
            <p className="text-xs text-red-600 mt-2">Armadillo: link not safe to open</p>
          )}
        </div>
      </div>
    </div>
  );
}

function parsedDisplay(url: string): string {
  try {
    return new URL(url.startsWith("http") ? url : `https://${url}`).href;
  } catch {
    return url;
  }
}
